import React, { useMemo, useRef, useState } from "react";
import { useMoney } from "../../../hooks/useMoney";
import { ByDateItem } from "../types";
import { MISChart } from "../components/MISChart";
import { chartTheme } from "../chartTheme";
import { SectionToggle, SectionViewMode } from "../components/SectionToggle";
import { TablePagination } from "../components/TablePagination";
import { ExportButton } from "../components/ExportButton";
import { exportChartsFromContainer, exportToCsv } from "../components/exportUtils";

interface DailyBreakdownSectionProps {
  creationByDate: ByDateItem[];
  usageByDate: ByDateItem[];
  expiryByDate: ByDateItem[];
}

const PAGE_SIZE = 10;

const ByDateSection: React.FC<{
  title: string;
  subtitle?: string;
  data: ByDateItem[];
  chartLabel: string;
  chartColor: { border: string; fill: string };
  chartType?: "line" | "bar";
  /** When true, no outer card border (nested inside another card). */
  embedded?: boolean;
  /** First segment in a grouped card has no top divider. */
  embeddedLeading?: boolean;
}> = ({
  title,
  subtitle,
  data,
  chartLabel,
  chartColor,
  chartType = "line",
  embedded = false,
  embeddedLeading = false,
}) => {
  const { code, number } = useMoney();
  const [mode, setMode] = useState<SectionViewMode>("graph");
  const [page, setPage] = useState(1);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const visible = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return data.slice(startIndex, startIndex + PAGE_SIZE);
  }, [page, data]);

  if (!data?.length) return null;

  const handleExportChart = () => {
    exportChartsFromContainer(chartContainerRef, title.toLowerCase().replace(/\s+/g, "-"));
  };

  const handleExportTable = () => {
    exportToCsv(
      `${title.toLowerCase().replace(/\s+/g, "-")}.csv`,
      ["Date", "Count", "Amount"],
      data.map((row) => [row.date, row.count, row.amount ?? 0])
    );
  };

  const body = (
    <>
      <div className="border-b border-cardborder bg-gray-50 px-6 py-4">
        <SectionToggle
          title={title}
          subtitle={subtitle}
          mode={mode}
          onChange={setMode}
          actions={
            <ExportButton
              label={mode === "graph" ? "Export PNG" : "Export CSV"}
              onClick={mode === "graph" ? handleExportChart : handleExportTable}
            />
          }
        />
      </div>
      <div className="px-6 py-5">
      {mode === "graph" ? (
        (() => {
          const filtered = data.filter(
            (row) => (row.count ?? 0) > 0 || (row.amount ?? 0) > 0
          );
          return filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No data to display</p>
          ) : (
            <div ref={chartContainerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="mb-2 text-xs text-gray-500">{chartLabel} (count)</p>
                <div className="h-56">
                  <MISChart
                    type={chartType}
                    labels={filtered.map((row) => row.date)}
                    datasets={[
                      {
                        label: "Count",
                        data: filtered.map((row) => row.count),
                        borderColor: chartColor.border,
                        backgroundColor: chartColor.fill,
                      },
                    ]}
                    heightClassName="h-56"
                  />
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs text-gray-500">{chartLabel} amount ({code})</p>
                <div className="h-56">
                  <MISChart
                    type="bar"
                    labels={filtered.map((row) => row.date)}
                    datasets={[
                      {
                        label: "Amount",
                        data: filtered.map((row) => row.amount ?? 0),
                        borderColor: chartTheme.slate,
                        backgroundColor: chartTheme.slateLight,
                      },
                    ]}
                    heightClassName="h-56"
                  />
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        <div className="overflow-hidden border border-cardborder">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-right font-semibold">Count</th>
                  <th className="px-3 py-2 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visible.map((row, idx) => (
                  <tr
                    key={row.date}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} transition-colors hover:bg-brand/5`}
                  >
                    <td className="px-3 py-2">{row.date}</td>
                    <td className="px-3 py-2 text-right">{row.count}</td>
                    <td className="px-3 py-2 text-right">
                      {code} {number((row.amount ?? 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={data.length}
            pageSize={PAGE_SIZE}
            itemLabel="days"
            onPageChange={setPage}
          />
        </div>
      )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className={embeddedLeading ? "" : "border-t border-cardborder"}>
        {body}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden border border-cardborder bg-white shadow-sm">
      {body}
    </div>
  );
};

export const DailyBreakdownSection: React.FC<DailyBreakdownSectionProps> = ({
  creationByDate,
  usageByDate,
  expiryByDate,
}) => {
  return (
    <>
      <ByDateSection
        title="Voucher Creation By Date"
        subtitle="Daily voucher creation count for the selected period"
        data={creationByDate}
        chartLabel="Created"
        chartColor={{ border: chartTheme.brand, fill: chartTheme.brandLight }}
      />
      <ByDateSection
        title="Voucher Usage By Date"
        subtitle="Daily voucher usage count for the selected period"
        data={usageByDate}
        chartLabel="Used"
        chartColor={{ border: chartTheme.indigo, fill: chartTheme.indigoLight }}
      />
      <ByDateSection
        title="Voucher Expiry By Date"
        subtitle="Vouchers expired by date for the selected period"
        data={expiryByDate}
        chartLabel="Expired"
        chartColor={{ border: chartTheme.amber, fill: chartTheme.amberLight }}
      />
    </>
  );
};

export const VoucherCreationByDateSection: React.FC<{ data: ByDateItem[] }> = ({ data }) => (
  <ByDateSection
    title="Voucher Creation By Date"
    subtitle="Daily voucher creation count for the selected period"
    data={data}
    chartLabel="Created"
    chartColor={{ border: chartTheme.brand, fill: chartTheme.brandLight }}
    chartType="bar"
  />
);

export const VoucherUsageByDateSection: React.FC<{ data: ByDateItem[] }> = ({ data }) => (
  <ByDateSection
    title="Voucher Usage By Date"
    subtitle="Daily voucher usage count for the selected period"
    data={data}
    chartLabel="Used"
    chartColor={{ border: chartTheme.indigo, fill: chartTheme.indigoLight }}
    chartType="bar"
  />
);

export const VoucherExpiryByDateSection: React.FC<{
  data: ByDateItem[];
  embedded?: boolean;
  embeddedLeading?: boolean;
}> = ({ data, embedded, embeddedLeading }) => (
  <ByDateSection
    title="Voucher Expiry By Date"
    subtitle="Vouchers expired by date for the selected period"
    data={data}
    chartLabel="Expired"
    chartColor={{ border: chartTheme.amber, fill: chartTheme.amberLight }}
    chartType="bar"
    embedded={embedded}
    embeddedLeading={embeddedLeading}
  />
);
